import React from 'react';

import {AnalyticsParameterValueOperation} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EMBEDDABLE_TYPE_TO_LABEL, EmbeddableParameterType} from 'insights/embeddings/EmbeddingConstants';
import getParameterType from 'insights/parameters/data/getParameterType';
import {PARAMETER_TYPE_TO_READABLE} from 'insights/parameters/ParameterConstants';
import ParameterPill, {
  EDIT_VALUE_TOOLTIP_TITLE,
  ParameterPillPartTooltipWrapper,
} from 'insights/parameters/ParameterPill';
import ValueJsonViewer from 'insights/parameters/ValueJsonViewer';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import {AnalyticsParameterValueTemplateGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterValueTemplateGraphQL';

export interface EmbeddedParameterPillProps {
  config: AnalyticsParameterConfigGraphQL;
  embeddingType: EmbeddableParameterType;
  onClick?: () => void;
  valueTemplate?: AnalyticsParameterValueTemplateGraphQL;
}

function EmbeddableValueTemplatePlaceholderViewer(
  props: Omit<EmbeddedParameterPillProps, 'valueTemplate'>
): JSX.Element {
  const disabled = props.onClick == null;
  const readableParameterType = PARAMETER_TYPE_TO_READABLE[props.embeddingType];
  const validationError =
    getParameterType(props.config) === props.embeddingType
      ? null
      : `Parameter type must be set to ${readableParameterType} for the embedded parameter value to default to the ${readableParameterType} being viewed.`;

  return (
    <ParameterPillPartTooltipWrapper
      className={validationError ? 'u-danger-color' : 'u-primary-color'}
      tooltipProps={{
        disabled,
        title: validationError ?? EDIT_VALUE_TOOLTIP_TITLE,
      }}
    >
      {EMBEDDABLE_TYPE_TO_LABEL[props.embeddingType]}
    </ParameterPillPartTooltipWrapper>
  );
}

export default function EmbeddedParameterPill(props: EmbeddedParameterPillProps): JSX.Element {
  const disabled = props.onClick == null;
  return (
    <ParameterPill
      onClickConfig={props.onClick}
      onClickValue={props.onClick}
      renderedConfig={
        <ParameterPillPartTooltipWrapper tooltipProps={{disabled, title: EDIT_VALUE_TOOLTIP_TITLE}}>
          {props.config.systemName}
        </ParameterPillPartTooltipWrapper>
      }
      renderedValue={
        props.valueTemplate?.valueOperation === AnalyticsParameterValueOperation.USE_EMBEDDING_ITEM ? (
          <EmbeddableValueTemplatePlaceholderViewer {...props} />
        ) : (
          <ValueJsonViewer
            config={props.config}
            disableTooltip={disabled}
            valueJson={props.valueTemplate?.valueJson ?? null}
          />
        )
      }
    />
  );
}
